import { useRef, useState, useEffect } from "react";
import { Star, Gem, Crown, ArrowUp, ArrowDown, ChevronRight } from "lucide-react";

const categoryIcons = {
  standard: Star,
  medium: Gem,
  premium: Crown,
};

export const CategoriesSection = ({ categories, selectedCategory, onCategoryChange, sortOrder, onSortChange, currencySymbol = "$", lang = "ru", t }) => {
  const scrollRef = useRef(null);
  const [canScroll, setCanScroll] = useState(false);

  const catNames = {
    standard: t ? t("cat.standard") : "Стандарт",
    medium: t ? t("cat.medium") : "Медиум",
    premium: t ? t("cat.premium") : "Премиум",
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => setCanScroll(el.scrollWidth > el.clientWidth + 10);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [categories]);

  const handleScrollHint = () => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: 200, behavior: "smooth" });
  };

  return (
    <section className="py-4 sm:py-8 bg-white border-b border-gray-100" data-testid="categories-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-hide"
            onScroll={() => {
              const el = scrollRef.current;
              if (el) setCanScroll(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
            }}
          >
            <button
              onClick={() => onCategoryChange("all")}
              className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm sm:text-base ${
                selectedCategory === "all"
                  ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-300"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              data-testid="category-all"
            >
              <span>{t ? t("products.all") : "Все букеты"}</span>
            </button>

            {categories.map((category) => {
              const IconComponent = categoryIcons[category.id] || Star;
              return (
                <button
                  key={category.id}
                  onClick={() => onCategoryChange(category.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm sm:text-base ${
                    selectedCategory === category.id
                      ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{catNames[category.id] || category.name}</span>
                </button>
              );
            })}

            <button
              onClick={onSortChange}
              className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200 ml-auto shrink-0 text-sm sm:text-base"
              data-testid="sort-price-btn"
            >
              {sortOrder === "asc" ? (
                <ArrowUp className="w-4 h-4" />
              ) : (
                <ArrowDown className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">{t ? t("products.price") : "Цена"}</span>
            </button>
          </div>

          {canScroll && (
            <button
              onClick={handleScrollHint}
              className="absolute right-0 top-0 bottom-2 w-12 flex items-center justify-end pr-1 sm:hidden bg-gradient-to-l from-white via-white/90 to-transparent"
              aria-label="Scroll for more"
              data-testid="scroll-hint-btn"
            >
              <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center animate-pulse">
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </div>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default CategoriesSection;
